import React, { useState, useEffect } from 'react';
import { Form, Button, Card } from 'react-bootstrap';
import { FaArrowRight } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import './PaymentCalculator.css';

const PaymentCalculator = ({ initialAmount, readOnlyAmount = false }) => {
    const navigate = useNavigate();
    const [amount, setAmount] = useState(initialAmount || 100000);
    const [term, setTerm] = useState(36); // Default to 36 months
    const [formattedAmount, setFormattedAmount] = useState('');
    const [isAmountInvalid, setIsAmountInvalid] = useState(false);
    const [monthlyPayments, setMonthlyPayments] = useState({
        12: 0,
        18: 0,
        24: 0,
        36: 0
    });
    
    // Format sayıyı binlik ayırıcı ile formatlama
    const formatNumber = (num) => {
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    };
    
    // String'den binlik ayırıcıları kaldır ve sayıya çevir
    const parseFormattedNumber = (str) => {
        return parseInt(str.replace(/\./g, ''), 10);
    };
    
    // Component yüklendiğinde ve amount değiştiğinde
    useEffect(() => {
        // Tutarı formatlı göster
        setFormattedAmount(formatNumber(amount));
        
        // Tutar geçerli mi kontrol et
        setIsAmountInvalid(amount > 2000000);
        
        // Aylık ödemeleri hesapla
        if (amount > 0) {
            const payments = {
                12: calculatePaymentForTerm(amount, 12),
                18: calculatePaymentForTerm(amount, 18),
                24: calculatePaymentForTerm(amount, 24),
                36: calculatePaymentForTerm(amount, 36)
            };
            setMonthlyPayments(payments);
        }
    }, [amount]);

    // Formatlı tutarı değiştirince amount state'ini güncelle
    const handleFormattedAmountChange = (e) => {
        const value = e.target.value.replace(/\./g, ''); // Noktalı kısımları temizle
        const numericValue = value.replace(/\D/g, ''); // Sadece rakamları al
        
        if (numericValue) {
            const parsedValue = parseInt(numericValue, 10);
            setAmount(parsedValue);
        } else {
            setAmount(0);
        }
    };

    // Verilen vade için aylık ödeme hesaplama
    const calculatePaymentForTerm = (loanAmount, months) => {
        // Vadeye göre aylık faiz oranları
        const interestRates = {
            12: 0.055, // %1.49 aylık
            24: 0.055, // %1.25 aylık
            36: 0.055, // %1.05 aylık
            48: 0.055  // %0.95 aylık
        };

        const rate = interestRates[months] || 0.01;
        const monthlyPayment = (loanAmount * (rate * Math.pow((1 + rate), months))) / (Math.pow((1 + rate), months) - 1);

        return monthlyPayment.toFixed(0);
    };

    // Başvuru sayfasına yönlendir
    const handleApplyNow = (e) => {
        e.preventDefault();
        
        if (isAmountInvalid) return; // Geçersiz tutar varsa işlemi durdur

        navigate('/application', {
            state: {
                vehicleAmount: amount,
                monthlyPayment: monthlyPayments[term],
                term: term
            }
        });
    };

    // Tutar aralığına göre gösterilecek vade seçeneklerini belirle
    const getAvailableTerms = () => {
        if (amount > 1290000 && amount <= 2000000) {
            return [12, 18, 24]; // 1.290.000 ile 2.000.000 arası
        } else if (amount <= 1290000) {
            return [12, 18, 24, 36]; // 1.290.000'dan küçük
        }
        return []; // Geçersiz tutar için boş dizi
    };

    // Seçilebilir vadeleri al
    const availableTerms = getAvailableTerms();

    // Geçerli vade seçili değilse, ilk geçerli vadeyi seç
    useEffect(() => {
        if (availableTerms.length > 0 && !availableTerms.includes(term)) {
            setTerm(availableTerms[0]);
        }
    }, [availableTerms, term]);

    return (
        <div className="calculator-content">
            <Form onSubmit={handleApplyNow}>
                <Form.Group className="mb-3">
                    <Form.Label>Peşinat Miktarını Girin</Form.Label>
                    <div className="input-group">
                        <span className="input-group-text">₺</span>
                        <Form.Control
                            type="text"
                            value={formattedAmount}
                            onChange={handleFormattedAmountChange}
                            className={isAmountInvalid ? "is-invalid" : ""}
                            readOnly={readOnlyAmount}
                        />
                    </div>
                    {isAmountInvalid && (
                        <div className="invalid-feedback d-block">
                            2.000.000 TL'den fazla kredi verilemiyor.
                        </div>
                    )}
                </Form.Group>

                {!isAmountInvalid && (
                    <Form.Group className="mb-4">
                        <Form.Label>Vade Sayısını Seçin</Form.Label>
                        <div className="term-options-compact">
                            {availableTerms.map(months => (
                                <div 
                                    key={months}
                                    className={`term-option-compact ${term === months ? 'active' : ''}`}
                                    onClick={() => setTerm(months)}
                                >
                                    <div className="term-month">{months} Ay</div>
                                    <div className="term-price">₺ {formatNumber(monthlyPayments[months])}</div>
                                </div>
                            ))}
                        </div>
                    </Form.Group>
                )}

                <div className="mt-4">
                    <div className="result-box mb-4 text-center">
                        <h5>Aylık Ödeme Tutarınız</h5>
                        <h2 className="amount">₺ {!isAmountInvalid ? formatNumber(monthlyPayments[term]) : "---"}</h2>
                        <small className="text-muted">
                            <div className="warning-notice mb-4">
                                <p className="warning-text">
                                    ⚠️ Hesaplama sonuçları bilgilendirme amaçlıdır. Kesin ödeme planı için başvuru yapınız.
                                </p>
                            </div>
                        </small>
                    </div>

                    <div className="d-grid gap-2">
                        <Button 
                            variant="primary" 
                            type="submit" 
                            size="lg" 
                            className="apply-btn"
                            disabled={isAmountInvalid}
                        >
                            Hemen Başvur <FaArrowRight className="ms-2" />
                        </Button>
                    </div>
                </div>
            </Form>
        </div>
    );
};

export default PaymentCalculator;